import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiSelect from "components/SuiSelect";
import FormField from "components/FormField";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import {
  useSoftUIController,
  setLang,
} from "context/theme";

import { Auth, AdminUser } from "api";

function AdminUserSettings({ }) {
  const { t, i18n } = useTranslation();
  const [, softUiDispatch] = useSoftUIController();
  const TITLE = t("Settings");
  const [me, setMe] = useState({});
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [language, setLanguage] = useState(i18n.language === "en" ? "" : String(i18n.language).toUpperCase());

  const getLanguageLabel = () => {
    if (language === "TC") {
      return t("Traditional Chinese");
    }
    if (language === "SC") {
      return t("Simplified Chinese");
    }
    return t("English");
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const { data } = await Auth.me();
        setMe(data);
        setLanguage(String(data.language !== "EN" ? data.language : "").toLocaleUpperCase());
      } catch (err) {
        console.error(err);
      }
      setIsLoading(false);
    })();
  }, []);

  const save = async () => {
    if ((password || password2) && password !== password2) {
      Swal.fire(t("Incorrect password entered"));
    } else {
      try {
        const data = {
          language: language || "EN",
        };
        [
          "id",
          "loginEmail",
          "lastName",
          "firstName",
          "mobile",
          "department",
          "position",
          "status",
        ].forEach((v) => {
          data[v] = me[v];
        });
        if ((password || password2)) {
          data.password = password;
        }
        await AdminUser.update(data);
        setPassword("");
        setPassword2("");
        setLang(softUiDispatch, String(language || "en").toLowerCase());
        Swal.fire(t("Settings Updated"));
      } catch (e) {
        console.error(e);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox my={1.5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card style={{ overflow: "visible" }}>
                <SuiBox p={3}>
                  <SuiTypography variant="h5">{TITLE}</SuiTypography>
                  <Divider />
                  <Grid item>
                    <FormField
                      label={t("Password")}
                      type={"password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                      }}
                    />
                    <FormField
                      label={t("Confirm Password")}
                      type={"password"}
                      value={password2}
                      onChange={(e) => {
                        setPassword2(e.target.value);
                      }}
                    />
                  </Grid>
                  <SuiTypography component="label" variant="caption" fontWeight="bold">
                    {t("Language")}
                  </SuiTypography>
                  <SuiSelect
                    value={{
                      label: getLanguageLabel(),
                      value: language,
                    }}
                    options={[
                      {
                        label: t("Traditional Chinese"),
                        value: "TC",
                      },
                      {
                        label: t("Simplified Chinese"),
                        value: "SC",
                      },
                      {
                        label: t("English"),
                        value: "",
                      },
                    ]}
                    onChange={(option) => {
                      setLanguage(option.value);
                    }}
                  />
                  <Grid item xs={12}>
                    <SuiBox display="flex" justifyContent="flex-end" mt={3}>
                      <SuiButton
                        variant="gradient"
                        color="info"
                        onClick={save}
                        disabled={isLoading}
                      >
                        {t("Update")}
                      </SuiButton>
                    </SuiBox>
                  </Grid>
                </SuiBox>
              </Card>
            </Grid>
          </Grid>
        </SuiBox>
        <Footer />
      </DashboardLayout>
    </>
  );
}

export default AdminUserSettings;
